import { SettingsPageGetDueDiligenceFunnelQuery } from '@/__generated__/graphql';
import { RedStarIcon } from '@/assets/icons/info-icons/redStar';
import { AiAssistantIcon } from '@/assets/static/icons/ai-assistant';
import { CopyIcon } from '@/assets/static/icons/copy-icon';
import { DragAndDropBigIcon } from '@/assets/static/icons/drag-and-drop-icon';
import ActionDropdown from '@/components/basicComponents/actionDropdown';
import { CheckBoxIcon } from '@/components/basicComponents/checkbox/icon';
import { Tooltip } from '@/components/basicComponents/tooltip';
import { blinkingFrame } from '@/components/table/styles';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import { useState } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { fieldsType } from './modals/addField';

const getDocxKey = (tierName: string, sectionName: string, fieldName: string) => {
  const docxKey = `${tierName.replace(/ /g, '_').trim().toLowerCase()}-${sectionName.replace(/ /g, '_').trim().toLowerCase()}-${fieldName
    .replace(/ /g, '_')
    .trim()
    .toLowerCase()}`;

  return docxKey;
};

interface SectionFieldsProps {
  tierName: string;
  fields: SettingsPageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]['sections'][number]['fields'];
  isLimitedEdits: boolean;
  section: SettingsPageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]['sections'][number];
  isDragDisabled: boolean;
  canDragging: { action: boolean; id: string };
  openPromptEditor: (
    field: SettingsPageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]['sections'][number]['fields'][number]
  ) => void;
  openModalWindow: (
    type: string,
    id: string,
    row:
      | SettingsPageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]['sections'][number]['fields'][number]
      | SettingsPageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]['sections'][number],
    rowType: string
  ) => void;
}

export const SectionFields = ({
  tierName,
  fields,
  isLimitedEdits,
  section,
  isDragDisabled,
  canDragging,
  openPromptEditor,
  openModalWindow
}: SectionFieldsProps) => {
  const { isMobile } = useResponsive();

  const [copiedFieldId, setCopiedFieldId] = useState<string | null>(null);

  const copyDocxKey = async (docxKey: string, fieldId: string) => {
    try {
      await navigator.clipboard.writeText(docxKey);
      setCopiedFieldId(fieldId);
    } catch (error: any) {
      console.error(error.message);
    } finally {
      setTimeout(() => {
        setCopiedFieldId(null);
      }, 2000);
    }
  };

  return (
    <Droppable droppableId={section.id} type="FIELDS">
      {(provided) => (
        <div ref={provided.innerRef} {...provided.droppableProps}>
          {fields.map((field, fieldIndex) => (
            <Draggable key={field.id} draggableId={field.id} index={fieldIndex} isDragDisabled={isLimitedEdits || isDragDisabled}>
              {(provided, snapshot) => {
                const docxKey = getDocxKey(tierName, section.name, field.name);
                return (
                  <div {...provided.dragHandleProps} {...provided.draggableProps} ref={provided.innerRef}>
                    <RowWrapper
                      isDragging={snapshot.isDragging}
                      disabled={false}
                      isMobile={isMobile}
                      loading={!canDragging.action && canDragging.id === field.id ? 1 : 0}
                    >
                      <InfoRowCell>
                        {!isLimitedEdits && (
                          <DragAndDropIconWrap id="dragIcon">
                            <DragAndDropBigIcon style={{ position: 'relative', top: '3px' }} fill={themeVar('font.action')} />
                          </DragAndDropIconWrap>
                        )}
                        {field.required ? <RedStarIcon width={10} height={10} /> : <NoRequired />}
                        {field.name}
                        <Tooltip title={docxKey}>
                          <CopyIconWrapper id="copyIcon" onClick={() => copyDocxKey(docxKey, field.id)}>
                            {copiedFieldId === field.id ? (
                              <CheckBoxIcon width={18} height={16} fill={themeVar('font.action')} />
                            ) : (
                              <CopyIcon fill={themeVar('font.action')} />
                            )}
                          </CopyIconWrapper>
                        </Tooltip>
                      </InfoRowCell>
                      <LinkedFieldWrap>
                        {fieldsType[field.type as keyof typeof fieldsType]}
                        {field.linkedField && (
                          <LinkedField>
                            (Linked Field: {field.linkedField.funnelTierSection.funnelTier.name} - {field.linkedField.funnelTierSection.name} -{' '}
                            {field.linkedField.name})
                          </LinkedField>
                        )}
                      </LinkedFieldWrap>
                      <ActionWrap isMobile={isMobile}>
                        <PromptWrap onClick={() => openPromptEditor(field)}>
                          <CountWrap>{field.promptGroup ? <AiAssistantIcon width={18} height={17} fill="#373F4E" /> : <>-</>}</CountWrap>
                        </PromptWrap>
                        <ActionDropdown
                          size="md"
                          options={
                            isLimitedEdits
                              ? [
                                  {
                                    name: 'Configure AI Assistant',
                                    onClick: () => openPromptEditor(field)
                                  },
                                  {
                                    name: 'Edit',
                                    onClick: () => openModalWindow('add-field', field.id, field, 'field')
                                  }
                                ]
                              : [
                                  {
                                    name: 'Configure AI Assistant',
                                    onClick: () => openPromptEditor(field)
                                  },
                                  {
                                    name: 'Edit',
                                    onClick: () => openModalWindow('add-field', field.id, field, 'field')
                                  },
                                  {
                                    name: 'Delete',
                                    onClick: () => openModalWindow('delete-field', field.id, section, 'section')
                                  }
                                ]
                          }
                        />
                      </ActionWrap>
                    </RowWrapper>
                  </div>
                );
              }}
            </Draggable>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
};

export const RowWrapper = styled.div<{
  disabled: boolean;
  isDragging: boolean;
  isMobile: boolean;
  loading?: boolean | number;
}>`
  display: flex;
  align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'center')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: 20px;
  padding: 20px 10px;
  border-bottom: 1px solid #c7cbd2;
  transition: 0.2s;
  background-color: #ffffff;
  box-shadow: ${({ isDragging }) => (isDragging ? '4px 0px 8px 0px rgba(0, 0, 0, 0.4)' : 'none')};

  opacity: ${({ loading }) => (loading ? 0.5 : 1)};
  pointer-events: ${({ loading }) => (loading ? 'none' : 'auto')};
  animation: ${({ loading }) => (loading ? blinkingFrame : null)} 1.5s infinite;

  & #actionDropdown {
    opacity: 0;
  }

  &:hover #dragIcon {
    opacity: 1;
  }

  &:hover #copyIcon {
    opacity: 1;
  }

  &:hover #aiIcon {
    opacity: 1;
  }

  &:hover #editTodoButton {
    opacity: 1;
  }

  &:hover #actionDropdown {
    opacity: 1;
  }

  &:hover {
    background-color: ${({ disabled }) => (disabled ? 'none' : '#f0f1f3')};
    transition: background-color 100ms ease;
  }
`;

export const InfoRowCell = styled.div`
  width: 40%;
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: #2e2e2e;
  display: flex;
  gap: 10px;
  align-items: center;
`;

export const DragAndDropIconWrap = styled.div`
  opacity: 0;
`;

const CountWrap = styled.div`
  background-color: #f4f5f6;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  color: #2e2e2e;
  width: 53px;
  height: 28px;
  font-size: 13px;
  margin-right: 6px;
`;

const LinkedFieldWrap = styled.div`
  display: flex;
  gap: 5px;
  font-size: 13px;
`;

const LinkedField = styled.div``;

export const ActionWrap = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-left: auto;
`;

const PromptWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  cursor: pointer;
`;

const NoRequired = styled.div`
  width: 10px;
`;

const CopyIconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
  cursor: pointer;
  opacity: 0;
`;
